import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography
} from "@material-ui/core";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import AddIcon from "@material-ui/icons/Add";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

// Catalogo de vozes da plataforma (so o super): cada voz e uma gravacao de
// referencia de 10 a 25 s. Os clientes escolhem na aba Voz do bloco de IA.
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const ouvir = caminho => new Audio(`${BACKEND}/${String(caminho || "").replace(/^\/+/, "")}`).play();

const VozesPlataforma = () => {
  const [vozes, setVozes] = useState([]);
  const [nome, setNome] = useState("");
  const [descricao, setDescricao] = useState("");
  const [amostra, setAmostra] = useState("");
  const [enviando, setEnviando] = useState(false);
  const arquivoRef = useRef(null);

  const carregar = async () => {
    try {
      const { data } = await api.get("/voz-natural/vozes/todas");
      setVozes(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const subir = async arquivo => {
    setEnviando(true);
    try {
      const fd = new FormData();
      fd.append("medias", arquivo);
      const { data } = await api.post("/flowbuilder/content", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const caminho = Array.isArray(data) ? data[0] : data?.url || data?.path || data;
      if (caminho) setAmostra(caminho);
    } catch (err) {
      toastError(err);
    } finally {
      setEnviando(false);
    }
  };

  const adicionar = async () => {
    try {
      const { data } = await api.post("/voz-natural/vozes", { nome, descricao, amostra });
      setVozes(data);
      setNome("");
      setDescricao("");
      setAmostra("");
      toast.success("Voz adicionada! Já aparece para os clientes.");
    } catch (err) {
      toastError(err);
    }
  };

  const mudar = async (id, campos) => {
    try {
      const { data } = await api.put(`/voz-natural/vozes/${id}`, campos);
      setVozes(data);
    } catch (err) {
      toastError(err);
    }
  };

  const remover = async voz => {
    if (!window.confirm(`Tirar a voz "${voz.nome}" do catálogo? Quem usa essa voz passa a responder em texto.`)) return;
    try {
      const { data } = await api.delete(`/voz-natural/vozes/${voz.id}`);
      setVozes(data);
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Paper variant="outlined" style={{ padding: 16 }}>
      <Typography variant="subtitle1" style={{ fontWeight: 600 }}>Vozes da plataforma (voz natural)</Typography>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
        Cada voz é uma gravação de 10 a 25 segundos, de uma pessoa falando naturalmente, sem música de fundo. Os clientes
        escolhem a voz na aba Voz do bloco de IA. Desligar uma voz tira ela da lista sem apagar.
      </Typography>

      {vozes.length === 0 && (
        <Typography variant="body2" style={{ marginBottom: 12 }}>Nenhuma voz cadastrada ainda.</Typography>
      )}
      {vozes.map(voz => (
        <Box key={voz.id} display="flex" alignItems="center" style={{ gap: 8, padding: "6px 0", borderBottom: "1px solid #eee" }}>
          <Tooltip title="Ouvir">
            <IconButton size="small" onClick={() => ouvir(voz.amostra)}><PlayArrowIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Box flex={1} minWidth={0}>
            <Typography variant="body2" style={{ fontWeight: 600 }}>{voz.nome}</Typography>
            {voz.descricao && <Typography variant="caption" color="textSecondary">{voz.descricao}</Typography>}
          </Box>
          <Tooltip title={voz.ativa !== false ? "Aparece para os clientes" : "Escondida"}>
            <Switch size="small" color="primary" checked={voz.ativa !== false} onChange={e => mudar(voz.id, { ativa: e.target.checked })} />
          </Tooltip>
          <Tooltip title="Tirar do catálogo">
            <IconButton size="small" onClick={() => remover(voz)}><DeleteOutlineIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Box>
      ))}

      <Box display="flex" flexWrap="wrap" alignItems="center" style={{ gap: 8, marginTop: 16 }}>
        <TextField size="small" variant="outlined" label="Nome da voz" placeholder="Ex.: Papai Noel" value={nome}
          onChange={e => setNome(e.target.value)} inputProps={{ maxLength: 60 }} />
        <TextField size="small" variant="outlined" label="Descrição (opcional)" placeholder="Ex.: voz grave e alegre"
          value={descricao} onChange={e => setDescricao(e.target.value)} inputProps={{ maxLength: 200 }} style={{ flex: 1, minWidth: 200 }} />
        <Button variant="outlined" startIcon={<CloudUploadIcon />} disabled={enviando}
          onClick={() => arquivoRef.current && arquivoRef.current.click()}>
          {enviando ? "Enviando…" : amostra ? "Trocar áudio" : "Enviar áudio"}
        </Button>
        <input ref={arquivoRef} type="file" accept="audio/*" style={{ display: "none" }}
          onChange={e => { const f = e.target.files && e.target.files[0]; e.target.value = ""; if (f) subir(f); }} />
        {amostra && (
          <IconButton size="small" onClick={() => ouvir(amostra)}><PlayArrowIcon fontSize="small" /></IconButton>
        )}
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={adicionar}
          disabled={!nome.trim() || !amostra || enviando}>
          Adicionar
        </Button>
      </Box>
    </Paper>
  );
};

export default VozesPlataforma;
